/**
 * Pure logic for field presence (see CONTEXT.md: Focus claim, Presence color).
 * Transport-agnostic: peers come in as already-decoded broadcast state, field
 * keys are opaque strings.
 */

/** One connected peer's announced presence state. */
export interface PeerPresence {
  userId: string;
  /** Field key the peer currently has focused, or null if just present. */
  fieldKey: string | null;
  /** Epoch ms when the current field was claimed; null when fieldKey is null. */
  claimedAt: number | null;
}

/** The claim a field key resolves to for display. */
export interface FieldClaim {
  userId: string;
}

/**
 * Resolve each field key to the claims shown for it. The local user's own
 * presence (any device) never produces a claim, and one user claiming from
 * several devices counts once. When several peers hold the same field
 * (co-editing), every claim is shown, ordered earliest-first — lower user id
 * on a timestamp tie — purely so every client renders the same order.
 */
export function computeClaims(
  peers: PeerPresence[],
  selfUserId: string,
): Record<string, FieldClaim[]> {
  const byField: Record<string, PeerPresence[]> = {};
  for (const p of peers) {
    if (p.userId === selfUserId || p.fieldKey == null) continue;
    (byField[p.fieldKey] ??= []).push(p);
  }
  return Object.fromEntries(
    Object.entries(byField).map(([key, claimants]) => {
      claimants.sort(compareClaims);
      const claims: FieldClaim[] = [];
      const seen = new Set<string>();
      for (const p of claimants) {
        if (seen.has(p.userId)) continue;
        seen.add(p.userId);
        claims.push({ userId: p.userId });
      }
      return [key, claims];
    }),
  );
}

const compareClaims = byRankThenUserId<PeerPresence>((p) => p.claimedAt ?? Infinity);

/**
 * Orders by a rank, breaking ties on the lower user id — the tie-break exists
 * purely so the resulting order is total, and therefore identical on every
 * client.
 */
function byRankThenUserId<T extends { userId: string }>(
  rank: (item: T) => number | string,
) {
  return (a: T, b: T): number => {
    const ar = rank(a);
    const br = rank(b);
    if (ar !== br) return ar < br ? -1 : 1;
    return a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0;
  };
}

/** One row of a template's collaborator roster. */
export interface RosterMember {
  userId: string;
  /** joined_at as stored (UTC timestamp text, lexicographically ordered). */
  joinedAt: string;
}

/**
 * Presence colors are a pure function of the synced collaborator roster:
 * slot = position in join order, mod the palette. Every client computes the
 * identical color for every user with no coordination — peer-to-peer
 * broadcast has no authority to arbitrate color races, and a deterministic
 * function of already-synced durable data needs none (ADR 0002). A joined-at
 * tie breaks on the lower user id so the order is total everywhere. Rosters
 * larger than the palette repeat colors; the claimant's name disambiguates.
 */
export function rosterColorSlots(
  members: RosterMember[],
  paletteSize: number,
): Map<string, number> {
  const sorted = [...members].sort(byRankThenUserId((m) => m.joinedAt));
  return new Map(sorted.map((m, i) => [m.userId, i % paletteSize]));
}
